import { useEffect, useId, useRef, useState } from 'react';
import type { JSX } from 'react';
import { Modal } from './Modal.js';
import { OptionList } from './OptionList.js';
import { IconChevronDown } from '../icons/index.js';
import { cn } from '../lib/cn.js';
import {
  BULK_MAX,
  BulkButton,
  BulkEmptyBlock,
  BulkProgressBlock,
  BulkReportBlock,
  BulkRunFooter,
  BulkSubline,
  BulkTooManyBlock,
  bulkCountLabel,
  useBulkRun,
} from './BulkRunner.js';
import type { BulkTarget } from './BulkRunner.js';
/*
 * L'EDITEUR DE CHAMP VIENT DE LA FICHE, IL N'EST PAS RECRIT ICI. Meme controle,
 * memes classes, memes conversions : la fiche et le lot ecrivent la meme
 * donnee, ils doivent la relire de la meme facon. Un second jeu d'editeurs
 * aurait diverge du premier au premier ajustement, sans qu'aucun build ne
 * rougisse.
 */
import { CellEditorControl, FORM_CONTROL_CLASSES, fromInput } from '../views/core/CellEditor.js';
import type { BulkEditField, Cell } from '../views/contract.js';

/**
 * LA FENETRE "MODIFICATION GROUPEE" : une valeur, un champ, toutes les fiches
 * choisies.
 *
 * TROIS TEMPS DEMANDES, CINQ ETATS A L'ECRAN. Les deux qu'on oublie toujours
 * sont PENDANT l'ecriture et APRES :
 *
 *   1. le CHAMP           (une liste deroulante, comme n'importe quel champ)
 *   2. la VALEUR          (elle n'apparait qu'une fois le champ repondu)
 *   3. la CONFIRMATION    (elle redit la valeur en toutes lettres)
 *   4. l'ECRITURE         (une fiche a la fois, avec sa progression)
 *   5. le COMPTE RENDU    (le cas partiel compris, fiches tombees NOMMEES)
 *
 * TOUT SE PASSE DANS LA MEME FENETRE : l'entete et le pied restent, seul le
 * CORPS change. On ne fait jamais croire qu'on a change de fenetre.
 *
 * LES TROIS TEMPS NE SONT PAS NUMEROTES. Le premier essai du prototype posait
 * des pastilles "1" et "2" ; la fenetre d'action native du produit n'a que des
 * libelles et des controles. Le premier temps est donc devenu un CHAMP comme
 * les autres ("Champ a modifier"), et le TYPE de l'editeur est annonce DANS
 * l'option ("Avancement, liste de choix") plutot que dans une colonne a part :
 * on sait ce qu'on va devoir saisir avant de s'engager. Le second champ
 * n'apparait qu'une fois le premier repondu : la progression se voit sans
 * qu'on la compte.
 *
 * LA VALEUR SE SAISIT SOUS LE CHAMP, dans la meme fenetre : les separer en
 * deux ecrans obligerait a revenir en arriere pour verifier ce qu'on a choisi.
 *
 * UN SEUL CHAMP A LA FOIS (decision du fondateur). Ce n'est pas une limite
 * technique : la boucle ecrit deja document par document et accepterait
 * n'importe quel nombre de champs. C'est une limite de LECTURE, la confirmation
 * devant tenir en une phrase qu'on relit avant d'ecrire sur douze fiches.
 *
 * ELLE VIT A LA RACINE DE L'ARBRE, montee par le chassis, jamais dans la
 * rangee 3 : celle-ci est `relative z-10` et ouvre un contexte d'empilement
 * dont un overlay ne sortirait plus. Rien ici ne suppose quoi que ce soit de
 * son parent.
 */

/* ------------------------------------------------------------------ */
/* Textes                                                              */
/* ------------------------------------------------------------------ */

/** Le titre, STABLE du premier temps au compte rendu : c'est la meme fenetre. */
const TITLE = 'Modification groupee';

/** L'entree neutre de la liste des champs. Elle ne choisit rien. */
const NO_FIELD = '';

/**
 * LA RAISON DE CHAQUE DESACTIVATION (G16 : un controle eteint dit POURQUOI, il
 * ne disparait jamais et ne reste jamais muet). L'ordre compte : on ne reproche
 * pas une valeur manquante a qui n'a pas encore choisi son champ.
 */
const NEED_FIELD = "Choisis d'abord le champ a modifier";
const NEED_VALUE = 'Saisis une valeur';
const NEED_NUMBER = 'Saisis un nombre valide';

/* ------------------------------------------------------------------ */
/* La fenetre                                                          */
/* ------------------------------------------------------------------ */

export interface BulkEditDialogProps {
  open: boolean;
  /** Sortie. IGNOREE pendant l'ecriture : on n'interrompt pas un lot en cours. */
  onClose: () => void;
  /** Le lot, GELE au clic par l'appelant. */
  ids: string[];
  /**
   * LES CHAMPS MODIFIABLES de la vue active. VIDE : la fenetre le DIT, elle ne
   * s'ouvre pas vide. Offrir puis refuser est pire que ne pas offrir, mais une
   * fenetre qui s'ouvre sur rien du tout laisse croire a une panne.
   */
  fields: BulkEditField[];
  /** La primitive A DOCUMENT UNIQUE de la porte. C'est ICI qu'on boucle dessus. */
  writeCell(id: string, fieldKey: string, value: unknown): Promise<void>;
  /**
   * NOMMER UNE FICHE. Sans elle, le compte rendu retombe sur les ids, et
   * "2 en echec" redevient un nombre qu'on ne peut pas rattraper.
   */
  labelOf?(id: string): string;
  /**
   * LE LOT EST FINI. C'est le signal du RENDU COMPLET : la porte se recharge,
   * les fiches ecrites quittent peut-etre la vue (le lot vient peut-etre de
   * changer le champ sur lequel l'ecran filtre ou trie), et l'invariant les
   * retire alors de la selection.
   */
  onRunEnd?(): void;
}

export function BulkEditDialog({
  open,
  onClose,
  ids,
  fields,
  writeCell,
  labelOf,
  onRunEnd,
}: BulkEditDialogProps): JSX.Element {
  /*
   * LE LOT EST GELE A L'OUVERTURE, noms compris.
   *
   * On ne relit JAMAIS `ids` apres coup : a la fin de l'ecriture, la porte se
   * recharge et l'invariant retire de la selection les lignes qui ont quitte le
   * jeu. Une fenetre qui relirait la prop verrait alors son lot fondre SOUS le
   * compte rendu, et annoncerait "3 modifiees" sur un total devenu 0.
   */
  const [targets, setTargets] = useState<BulkTarget[]>([]);
  const [fieldKey, setFieldKey] = useState<string>(NO_FIELD);
  const [draft, setDraft] = useState('');
  const [asking, setAsking] = useState(false);
  const [choosingField, setChoosingField] = useState(false);
  const fieldButtonRef = useRef<HTMLButtonElement>(null);
  const fieldLabelId = useId();
  const fieldValueId = useId();
  /*
   * L'IDENTITE DU LOT DEMANDE, prise PAR VALEUR et pas par reference.
   *
   * C'EST ELLE QUI ARME LA FENETRE, et plus le simple passage de `open` a vrai.
   * L'ancienne garde (`open === openedRef.current`) laissait passer un trou : la
   * fenetre DEJA ouverte sur un compte rendu ne voyait pas un second geste, donc
   * elle gardait l'ancien lot et l'ancien bilan sous les yeux de quelqu'un qui
   * venait d'en demander un autre.
   *
   * PAR VALEUR : `ids` arrive du chassis en `ids={bulkEdit?.ids ?? []}`, donc
   * avec une reference NEUVE a chaque rendu quand il n'y a pas de lot ; comparer
   * les references rearmerait la fenetre en boucle.
   */
  const requestKey = ids.join(' ');
  const armedRef = useRef<string | null>(null);

  const field = fields.find((entry) => entry.key === fieldKey);
  const count = targets.length;
  const tooMany = count > BULK_MAX;
  const empty = count === 0;
  const noFields = fields.length === 0;

  /*
   * CE QUI PARTIRA VRAIMENT EN BASE, relu par la MEME conversion que la fiche
   * (`fromInput`) : une date PURE reste une chaine `YYYY-MM-DD` (elle ne
   * traverse pas un fuseau), un nombre invalide rend `undefined` et ne s'ecrit
   * pas. On le calcule ici, une fois, plutot qu'au moment d'ecrire : le bouton
   * doit pouvoir dire qu'une saisie ne passera pas AVANT de lancer le lot.
   */
  const value: Cell | undefined = field == null ? undefined : fromInput(field.editor, draft);

  /*
   * LA BOUCLE N'EST PAS ICI : elle est dans `BulkRunner`, ecrite une fois pour
   * les trois gestes. Cette fenetre ne fournit que la primitive a UN document.
   *
   * La fonction est recreee a chaque rendu, et c'est sans consequence : le
   * moteur en prend une COPIE AU LANCEMENT, donc la boucle ecrit du premier au
   * dernier document le champ et la valeur qui ont ete CONFIRMES, meme si cette
   * fenetre se rend a nouveau entre-temps. La garde ci-dessous ne devrait donc
   * jamais parler ; elle reste parce qu'un lot qui n'a rien a ecrire doit tomber
   * en echec NOMME, pas ecrire n'importe quoi.
   */
  const run = useBulkRun(async (id: string): Promise<void> => {
    if (field == null || value === undefined) {
      throw new Error("Aucune valeur a ecrire : le champ ou la valeur a change.");
    }
    await writeCell(id, field.key, value);
  }, onRunEnd);
  const { reset, busy } = run;

  /*
   * TOUT SE REMET A ZERO SUR UN LOT NEUF, jamais a la fermeture : le Modal reste
   * monte le temps de son fondu de sortie, et vider le corps a ce moment-la
   * ferait clignoter la fenetre pendant qu'elle disparait (on la verrait
   * repasser par son premier temps en s'en allant).
   *
   * "UN LOT NEUF", et pas "une ouverture" : la fenetre peut deja etre ouverte,
   * arretee sur un compte rendu, quand un second geste arrive.
   */
  useEffect(() => {
    /* Fermee : on oublie ce qui a ete arme, la prochaine ouverture repart a
       neuf, meme sur le meme lot. */
    if (!open) {
      armedRef.current = null;
      return;
    }
    if (armedRef.current === requestKey) return;
    /* UN LOT EST EN COURS : on ne remet RIEN a zero sous ses pieds. Le champ et
       la valeur sont encore ceux du rejeu possible, et le compte rendu se lit
       encore. La demande n'est pas consommee : cet effet se rejoue quand `busy`
       retombe, et la fenetre s'arme alors sur le nouveau lot. */
    if (busy) return;
    armedRef.current = requestKey;
    setTargets(ids.map((id) => ({ id, label: labelOf?.(id) ?? id })));
    setFieldKey(NO_FIELD);
    setDraft('');
    setAsking(false);
    setChoosingField(false);
    reset();
  }, [open, requestKey, busy, ids, labelOf, reset]);

  /**
   * CHANGER DE CHAMP REMET LA VALEUR A ZERO. Garder la saisie precedente
   * ecrirait un texte dans un nombre, ou une date dans un catalogue.
   *
   * Un CATALOGUE part sur sa premiere entree : un `select` natif ne peut pas
   * rester vide sans une option fantome, et la confirmation redit de toute
   * facon la valeur en toutes lettres avant d'ecrire.
   */
  const pickField = (key: string): void => {
    setFieldKey(key);
    const picked = fields.find((entry) => entry.key === key);
    if (picked != null && (picked.editor === 'select' || picked.editor === 'lien')) {
      setDraft(picked.options?.[0]?.value ?? '');
      return;
    }
    setDraft('');
  };

  /** La valeur EN TOUTES LETTRES : le libelle d'un catalogue, jamais sa cle. */
  const spelled =
    field?.editor === 'multiselect' && draft.trim() === '' ? 'Aucun choix' :
    field != null && ['select', 'lien', 'multiselect'].includes(field.editor)
      ? draft.split('\n').map(value => field.options?.find(option => option.value === value)?.label ?? value).join(', ')
      : draft;

  /** Pourquoi le bouton d'application est eteint, ou `null` s'il est allume. */
  const blocked =
    field == null ? NEED_FIELD : draft.trim() === '' && field.editor !== 'multiselect' ? NEED_VALUE : value === undefined ? NEED_NUMBER : null;

  const failed = run.failures.length;
  const writing = run.phase === 'running';
  const reported = run.phase === 'done';

  /* Le pied, temps par temps. Le bouton de SORTIE ne disparait jamais : pendant
     l'ecriture il s'ETEINT AVEC SA RAISON (G16), sinon la fenetre semblerait
     avoir change de forme au pire moment.

     CHAQUE BOUTON PASSE PAR `BulkButton`, ET C'EST LA TOUTE LA DIFFERENCE : le
     `Button` du socle porte `disabled:pointer-events-none`, donc un `title` pose
     directement dessus ne s'affiche JAMAIS quand il est eteint. Les deux raisons
     que le brief exige nommement ("Choisis d'abord le champ a modifier",
     "Saisis une valeur") et celle de "Fermer" pendant l'ecriture etaient
     lisibles nulle part. La coque les porte, le bouton les garde en
     `aria-label`. */
  const footer = ((): JSX.Element => {
    /* L'ORDRE DE CES TESTS EST LE MEME QUE CELUI DU CORPS, et il commence par
       l'ECRITURE : un lot en cours doit dominer tout le reste. Si la vue
       recalculait ses champs modifiables pendant que le lot s'ecrit, un test
       pose plus haut rendrait un pied de sortie ACTIF au milieu d'une
       ecriture, c'est-a-dire exactement ce que ce chantier interdit.

       LE PIED DE L'ECRITURE ET DU BILAN EST CELUI DE `BulkRunner`, partage : il
       etait ecrit deux fois, mot pour mot, dans les deux fenetres. */
    if (writing || reported) {
      return (
        <BulkRunFooter
          phase={run.phase}
          failures={failed}
          onClose={onClose}
          onRetry={run.retryFailures}
        />
      );
    }
    if (noFields || tooMany || empty) {
      return <BulkButton label="Fermer" variant="secondary" onClick={onClose} />;
    }
    if (asking) {
      return (
        <>
          <BulkButton label="Revenir" variant="secondary" onClick={() => setAsking(false)} />
          <BulkButton
            label={`Modifier ${bulkCountLabel(count)}`}
            variant="primary"
            onClick={() => run.start(targets)}
          />
        </>
      );
    }
    return (
      <>
        <BulkButton label="Annuler" variant="secondary" onClick={onClose} />
        {/* LE BOUTON PORTE LE NOMBRE : c'est la derniere occasion de voir sur
            combien de fiches on agit avant la confirmation. Et quand il est
            eteint, il porte SA RAISON, qui s'affiche vraiment. */}
        <BulkButton
          label={`Appliquer a ${bulkCountLabel(count)}`}
          variant="primary"
          reason={blocked}
          onClick={() => setAsking(true)}
        />
      </>
    );
  })();

  return (
    <Modal
      open={open}
      /* LA SORTIE EST FILTREE ICI : le Modal ferme sur `Echap` et sur le voile,
         et il n'a pas a connaitre nos temps. `Echap` ferme, SAUF pendant
         l'ecriture : on n'interrompt pas un lot en cours par un reflexe. */
      onClose={() => {
        if (busy) return;
        if (choosingField) {
          setChoosingField(false);
          fieldButtonRef.current?.focus();
          return;
        }
        onClose();
      }}
      title={TITLE}
      size="md"
      footer={footer}
    >
      {/*
       * LE CORPS, ET UNE SEULE REGLE DE RENDU A NE PAS CASSER : le pied se
       * relit a chaque frappe (le bouton s'allume, sa raison change), et le
       * corps NE DOIT PAS ETRE REDESSINE pour autant, sinon le champ de saisie
       * perd le focus au premier caractere.
       *
       * En React, la regle se tient en NE FABRIQUANT AUCUN COMPOSANT DANS CE
       * RENDU et en gardant la forme de l'arbre stable pendant la frappe : le
       * meme element, a la meme place, garde son noeud DOM et donc son focus.
       * Les deux pieges a ne pas reintroduire sont une fonction composant
       * declaree ici (elle change d'identite a chaque rendu, donc React
       * remonte tout) et une `key` qui bougerait avec la saisie. La `key` de
       * l'editeur ne porte QUE la cle du champ : elle ne change qu'au
       * changement de champ, ou l'on VEUT justement repartir a neuf.
       */}
      <div className="flex flex-col gap-5">
        {reported ? (
          <>
            <BulkSubline>{failed === 0 ? 'Termine.' : 'Termine, avec des echecs.'}</BulkSubline>
            {/* LE COMPTE RENDU DECRIT LE DERNIER LOT JOUE, pas la somme des
                tentatives : apres un rejeu de 2 fiches dont 1 retombe, il dit
                "1 fiche modifiee, 1 en echec" et NOMME celle qui reste. C'est
                ce qui permet d'enchainer les rejeux sans jamais reecrire une
                fiche deja bonne. */}
            <BulkReportBlock verb="edit" total={run.total} failures={run.failures} />
          </>
        ) : writing ? (
          <>
            <BulkSubline>Ne ferme pas cette fenetre.</BulkSubline>
            {/* LES TOMBEES SE COMPTENT DES LA PROGRESSION : le compteur avance a
                chaque document TENTE, echec compris, donc "9 sur 12" tout seul
                laisserait croire a neuf ecritures reussies. */}
            <BulkProgressBlock done={run.done} total={run.total} failed={failed} />
          </>
        ) : noFields ? (
          <>
            <BulkSubline>Rien a modifier ici.</BulkSubline>
            <p className="text-[0.9375rem] leading-normal text-ink-900 [text-wrap:pretty]">
              Aucun champ de cette vue ne se modifie. Le lot ne peut donc rien y ecrire.
            </p>
          </>
        ) : tooMany ? (
          <>
            <BulkSubline>Lot trop grand.</BulkSubline>
            <BulkTooManyBlock count={count} />
          </>
        ) : empty ? (
          <>
            <BulkSubline>Lot vide.</BulkSubline>
            <BulkEmptyBlock />
          </>
        ) : asking ? (
          <>
            <BulkSubline>Derniere verification.</BulkSubline>
            {/* LA CONFIRMATION REDIT LA VALEUR EN TOUTES LETTRES. Un "Appliquer"
                qui ne repete pas ce qu'il va ecrire fait ecrire de travers. */}
            <p className="text-[0.9375rem] leading-normal text-ink-900 [text-wrap:pretty]">
              {'« '}
              {field?.label}
              {' » passe a '}
              <strong className="font-semibold">{spelled}</strong>
              {` sur ${bulkCountLabel(count)}. Les valeurs actuelles sont remplacees.`}
            </p>
          </>
        ) : (
          <>
            <BulkSubline>
              {`${bulkCountLabel(count)} ${count > 1 ? 'selectionnees' : 'selectionnee'}.`}
            </BulkSubline>

            {/* LE PREMIER TEMPS EST UN CHAMP COMME LES AUTRES. Ce n'est PAS un
                `CellEditorControl` : il ne saisit pas une valeur, il choisit
                sur quoi on va en saisir une. Il en reprend en revanche la
                BOITE, au token pres, pour que les deux champs de cette fenetre
                se ressemblent. */}
            <div className="flex flex-col gap-2">
              <span id={fieldLabelId} className="text-sm font-medium leading-[1.3] text-ink-700">
                Champ a modifier
              </span>
              <div className="relative">
                <button
                  ref={fieldButtonRef}
                  type="button"
                  aria-labelledby={`${fieldLabelId} ${fieldValueId}`}
                  aria-haspopup="listbox"
                  aria-expanded={choosingField}
                  className={cn(FORM_CONTROL_CLASSES, 'flex items-center justify-between gap-2 text-left')}
                  onClick={() => setChoosingField(true)}
                  onKeyDown={(event) => {
                    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
                    event.preventDefault();
                    setChoosingField(true);
                  }}
                >
                  <span id={fieldValueId} className="truncate">{field?.editorLabel ?? 'Choisir un champ...'}</span>
                  <IconChevronDown size={14} className="shrink-0" />
                </button>
                {choosingField && <div className="[&>span>div]:relative [&>span>div]:inset-auto [&>span>div]:mb-0 [&>span>div]:mt-1.5 [&>span>div]:w-full [&>span>div]:min-w-0 [&>span>div]:max-w-none"><OptionList
                  recherche
                  options={fields.map((entry) => ({ value: entry.key, label: entry.editorLabel }))}
                  value={fieldKey || null}
                  vide="Aucun champ"
                  onPick={(key) => {
                    if (key == null) return;
                    pickField(key);
                    setChoosingField(false);
                    fieldButtonRef.current?.focus();
                  }}
                  onClose={() => setChoosingField(false)}
                /></div>}
              </div>
            </div>

            {/* LE SECOND CHAMP N'APPARAIT QU'UNE FOIS LE PREMIER REPONDU : la
                progression se voit sans qu'on la compte, et sans pastille
                numerotee. */}
            {field != null && (
              <div className="flex flex-col gap-2">
                <span className="text-sm font-medium leading-[1.3] text-ink-700">
                  {field.editor === 'multiselect' ? 'Choix qui remplaceront les valeurs actuelles' : 'Nouvelle valeur'}
                </span>
                {/*
                  `ECHAP` DANS LE CHAMP ANNULE LA SAISIE, IL NE FERME PAS LA
                  FENETRE. C'est le partage de roles que la FICHE tient deja pour
                  la meme touche (`CellEditor.tsx`, `FieldEditorControl` : `Echap`
                  dans un editeur annule la saisie et s'arrete la, avec un
                  `stopPropagation` explicite ; hors editeur, la meme touche ferme
                  la fiche). La meme touche, dans le meme produit, ne peut pas
                  faire l'inverse d'un ecran a l'autre.

                  Le cout etait reel, et c'est le multiligne qui le payait : dix
                  lignes ecrites pour douze fiches disparaissaient d'un coup, avec
                  le champ choisi, sur un simple reflexe.

                  UNE PREMIERE FRAPPE VIDE LE CHAMP, une seconde ferme la
                  fenetre : le second `Echap` ne trouve plus rien a annuler, donc
                  il remonte jusqu'au `Modal`, qui ferme.

                  UN CATALOGUE EST HORS DE CE PARTAGE : il ne se SAISIT pas, il se
                  choisit, il n'a donc rien a perdre, et le vider le laisserait sur
                  une valeur fantome. `Echap` y ferme directement.
                */}
                <CellEditorControl
                  key={field.key}
                  ariaLabel="Nouvelle valeur"
                  editor={field.editor}
                  variant="form"
                  draft={draft}
                  onDraftChange={setDraft}
                  options={field.options ?? []}
                  onKeyDown={(event) => {
                    if (event.key !== 'Escape') return;
                    if (field.editor === 'select' || draft === '') return;
                    event.stopPropagation();
                    setDraft('');
                  }}
                />
                {/* L'aide vit SOUS son champ, jamais au-dessus : on la lit
                    apres avoir vu ce qu'on remplit. */}
                <span className="text-[0.8125rem] leading-[1.4] text-ink-500 [text-wrap:pretty]">
                  La meme valeur sera ecrite sur toutes les fiches choisies.
                </span>
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}

export default BulkEditDialog;
